import { Check, LoaderCircle, Pause, Play, SkipBack, VolumeX } from 'lucide-react'
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type RefObject,
  useEffect,
  useId,
  useRef,
} from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { cn } from '@/utils/cn'
import { useStableCallback } from '../../questioning/use-stable-callback'
import { MICRO_LABEL, ON_PRIMARY, SELECTED, TEXT_BODY_SM, TEXT_CAPTION } from '../../recipes'
import { ScopeLine } from '../../ScopeLine'
import { KeyHint } from '../../tattoo-match/PairDecisionPanel'
import { type VoiceListeningSide, VoicePairChannel } from './VoicePairChannel'
import { sideIndex, VOICE_PAIR_SIDES, type VoicePairTransport } from './voice-pair-model'
import { KEYS_ONLY, PAIR_CARD, PAIR_MAIN_COLUMN } from './voice-pair-recipes'
import type { VoicePairSideId } from './voice-pair-types'

const COPY = 'humint.voice.match'

/** Keys that put a side in the transport. Digits: a letter beside S and D would decide by slip. */
const SIDE_KEYS: Record<VoicePairSideId, string> = { a: '1', b: '2' }

export interface VoicePairListeningProps {
  /** The pair's two sides, A first. */
  sides: readonly [VoiceListeningSide, VoiceListeningSide]
  transport: VoicePairTransport
}

/** An open overlay keeps its own keys. */
const OVERLAY = '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"]'

/** Whether a typed digit belongs to a field. The transport's own sliders are no such field. */
function typesInto(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target instanceof HTMLInputElement) return target.type !== 'range'
  return target.isContentEditable || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT'
}

/** The side a key press asks for: 1 or 2 pressed alone, outside text fields and overlays. */
function keySide(event: KeyboardEvent): VoicePairSideId | null {
  if (event.defaultPrevented || event.repeat || event.altKey || event.ctrlKey || event.metaKey)
    return null
  if (typesInto(event.target)) return null
  if (event.target instanceof Element && event.target.closest(OVERLAY)) return null
  return VOICE_PAIR_SIDES.find(side => SIDE_KEYS[side] === event.key) ?? null
}

/**
 * 1 and 2, listened to on the whole page like the decision keys, so they keep working after a
 * click took the focus elsewhere. A press also puts the keyboard on the transport itself, off
 * whichever control had it: the space bar that follows then plays the side just chosen, and is
 * never the press of the other side's segment or of *From the start*.
 */
function useSideKeys(
  group: RefObject<HTMLElement | null>,
  onSelect: (side: VoicePairSideId) => void
) {
  const onKeyDown = useStableCallback((event: KeyboardEvent) => {
    const shown = (group.current?.getClientRects().length ?? 0) > 0
    const side = shown ? keySide(event) : null
    if (!side) return
    event.preventDefault()
    onSelect(side)
    group.current?.focus({ preventScroll: true })
  })
  useEffect(() => {
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onKeyDown])
}

/** One segment of the A | B switch: 48 px at every width, it is the screen's main control. */
const SIDE_SEGMENT = `h-12 min-w-fit flex-1 gap-2 rounded-lg border border-transparent px-3 ${TEXT_BODY_SM} font-medium text-muted-foreground hover:bg-accent data-[state=on]:text-foreground ${SELECTED}`

/**
 * The A | B switch: a single-choice toggle group, so the arrow keys move between the sides. The
 * side in the transport carries the Check and the copper wash; a side whose clip is on its way
 * spins in its own segment while the other one goes on.
 */
function SideSwitch({ transport }: { transport: VoicePairTransport }) {
  const { t } = useTranslation()
  const { active, actions } = transport
  return (
    <ToggleGroup
      type="single"
      spacing={1}
      value={active}
      aria-label={t(`${COPY}.transport.switch`)}
      onValueChange={next => {
        const side = VOICE_PAIR_SIDES.find(id => id === next)
        if (side) actions.onSelect(side)
      }}
      className="w-auto min-w-0 flex-1 rounded-xl border border-border bg-secondary p-1"
    >
      {VOICE_PAIR_SIDES.map(side => (
        <ToggleGroupItem
          key={side}
          value={side}
          aria-keyshortcuts={SIDE_KEYS[side]}
          className={SIDE_SEGMENT}
        >
          {side === active && <Check aria-hidden className="size-4 text-primary-strong" />}
          {t(`${COPY}.side.${side}`)}
          {transport.sides[sideIndex(side)].audio === 'loading' && (
            <LoaderCircle aria-hidden className="size-4 animate-spin motion-reduce:animate-none" />
          )}
          <span className={KEYS_ONLY}>
            <KeyHint keys={[SIDE_KEYS[side]]} />
          </span>
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  )
}

/**
 * Pinned 24 px over the bottom of the scrolling page while narrow, with its own surface drawn
 * under it: clear of the shell's raised check disc and of a phone's home indicator, and nothing
 * scrolls through the gap. The card clips that strip away once the bar rests at the card's end.
 */
const TRANSPORT_BAR =
  'sticky bottom-6 z-10 flex items-center gap-2 border-border border-t bg-card p-3 after:absolute after:inset-x-0 after:top-full after:h-6 after:bg-card @3xl/voicepair:static @3xl/voicepair:px-4 @3xl/voicepair:after:hidden @5xl/voicepair:gap-3'

/**
 * Play or pause the side in the transport: the screen's primary. Its cap names the space bar in
 * words — nothing is printed on that key, and its sign is too small to read at a cap's size.
 */
function PlayButton({
  playing,
  disabled,
  onToggle,
}: {
  playing: boolean
  disabled: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const PlayIcon = playing ? Pause : Play
  return (
    <Button
      variant="solid"
      size="icon"
      disabled={disabled}
      onClick={onToggle}
      aria-keyshortcuts="Space"
      aria-label={t(`humint.questioning.player.${playing ? 'pause' : 'play'}`)}
      className={cn('size-12 shrink-0 gap-2 @5xl/voicepair:w-auto @5xl/voicepair:px-4', ON_PRIMARY)}
    >
      <PlayIcon aria-hidden className="size-5 fill-current" />
      <span className={KEYS_ONLY}>
        <KeyHint keys={[t(`${COPY}.transport.spaceKey`)]} />
      </span>
    </Button>
  )
}

/** Back to the start of the side in the transport; its words and the Home cap from 1024 px. */
function RestartButton({ disabled, onRestart }: { disabled: boolean; onRestart: () => void }) {
  const { t } = useTranslation()
  return (
    <Button
      variant="outline"
      size="icon"
      disabled={disabled}
      onClick={onRestart}
      aria-keyshortcuts="Home"
      aria-label={t(`${COPY}.transport.restart`)}
      className="size-12 shrink-0 gap-2 @5xl/voicepair:w-auto @5xl/voicepair:px-3"
    >
      <SkipBack aria-hidden className="size-5" />
      <span className="hidden text-sm @5xl/voicepair:inline">{t(`${COPY}.transport.restart`)}</span>
      <span className={KEYS_ONLY}>
        <KeyHint keys={['Home']} />
      </span>
    </Button>
  )
}

/**
 * The one transport: play or pause, the A | B switch, and back to the start of the side in it.
 * It stays at the thumb on a phone (pinned to the bottom while the sides scroll); from 1024 px
 * every control shows its key.
 */
function TransportBar({
  transport,
  disabled,
}: {
  transport: VoicePairTransport
  disabled: boolean
}) {
  const { active, actions } = transport
  const side = transport.sides[sideIndex(active)]
  const silent = disabled || side.audio === 'unavailable'
  return (
    <div className={TRANSPORT_BAR}>
      <PlayButton
        playing={side.playback.playing}
        disabled={silent}
        onToggle={actions.onTogglePlay}
      />
      <SideSwitch transport={transport} />
      <RestartButton disabled={silent} onRestart={() => actions.onSeek(active, 0)} />
    </div>
  )
}

/**
 * What stands between the listener and the sound, in one place: a side that cannot be played
 * (the other still works), neither side, or nothing fetched yet. And always that listening is
 * recorded: every clip fetched is a row in the audit trail.
 */
function TransportNotes({ transport }: { transport: VoicePairTransport }) {
  const { t } = useTranslation()
  const gone = VOICE_PAIR_SIDES.filter(id => transport.sides[sideIndex(id)].audio === 'unavailable')
  const waiting = transport.sides[sideIndex(transport.active)].audio === 'none'
  return (
    <div className="flex flex-col gap-1.5 px-4 pb-3">
      {gone.length > 0 && (
        <p
          role="status"
          className={cn(
            TEXT_CAPTION,
            'flex items-start gap-1.5 font-medium text-warning-foreground'
          )}
        >
          <VolumeX aria-hidden className="mt-0.5 size-3.5 shrink-0" />
          {gone.length === 2
            ? t(`${COPY}.audio.bothUnavailable`)
            : t(`${COPY}.audio.unavailable`, { side: t(`${COPY}.side.${gone[0]}`) })}
        </p>
      )}
      {waiting && gone.length < 2 && (
        <p className={cn(TEXT_CAPTION, 'text-muted-foreground')}>{t(`${COPY}.audio.none`)}</p>
      )}
      <ScopeLine>{t(`${COPY}.audio.audited`)}</ScopeLine>
    </div>
  )
}

/** The two sides: beside each other from 1024 px, stacked below; each moves its own playhead. */
function TwoSides({ sides, transport }: VoicePairListeningProps) {
  return (
    <div className="grid grid-cols-1 gap-3 p-4 @5xl/voicepair:grid-cols-2">
      {VOICE_PAIR_SIDES.map(id => (
        <VoicePairChannel
          key={id}
          id={id}
          side={sides[sideIndex(id)]}
          state={transport.sides[sideIndex(id)]}
          control={{
            active: transport.active === id,
            onSeek: ms => transport.actions.onSeek(id, ms),
          }}
        />
      ))}
    </div>
  )
}

/**
 * The listening card. Clipped, not hidden: the pinned transport still follows the page's own
 * scrolling. With the keyboard in it, its border turns copper.
 */
const LISTENING_CARD = `${PAIR_CARD} ${PAIR_MAIN_COLUMN} flex flex-col overflow-clip outline-none focus-visible:border-primary`

/**
 * Whether the space bar is the focused control's own press. A button's is — but not the segment
 * of the side already in the transport: pressing that again changes nothing, so there the space
 * bar plays, as it should right after a click chose the side.
 */
const ownsSpace = (target: HTMLElement): boolean =>
  target.tagName === 'BUTTON' && target.getAttribute('aria-checked') !== 'true'

/** Space and Home inside the transport. A focused button keeps its own space: it is its press. */
function transportKey(event: ReactKeyboardEvent<HTMLElement>, transport: VoicePairTransport) {
  if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return
  const target = event.target as HTMLElement
  // Inside a comparison dialog the group owns these keys; the global handler leaves overlays alone.
  const side = VOICE_PAIR_SIDES.find(id => SIDE_KEYS[id] === event.key)
  if (side && !typesInto(target)) {
    event.preventDefault()
    if (!event.repeat) transport.actions.onSelect(side)
    return
  }
  if (event.key === ' ' && !ownsSpace(target)) {
    event.preventDefault()
    if (!event.repeat) transport.actions.onTogglePlay()
  }
  // A focused slider goes to its own start by itself.
  if (event.key === 'Home' && target.tagName !== 'INPUT') {
    event.preventDefault()
    transport.actions.onSeek(transport.active, 0)
  }
}

/**
 * A/B listening, the working tool of the comparison: one transport over the two clips. The two
 * sides stand beside each other (stacked when narrow), each with its own playhead; the switch
 * puts one of them in the transport and it goes on where it was left. Keys: 1 and 2 choose the
 * side anywhere on the page; the space bar plays or pauses and Home goes back to the start while
 * the keyboard is inside this group. It holds no audio element: what sounds is the host's.
 */
export function VoicePairListening({ sides, transport }: VoicePairListeningProps) {
  const { t } = useTranslation()
  const headingId = useId()
  const keysId = useId()
  const group = useRef<HTMLDivElement>(null)
  const silent = transport.sides.every(side => side.audio === 'unavailable')
  useSideKeys(group, transport.actions.onSelect)
  // A desktop host puts the keyboard here on opening, so the space bar plays at once.
  const { autoFocus } = transport
  useEffect(() => {
    if (autoFocus) group.current?.focus({ preventScroll: true })
  }, [autoFocus])
  return (
    <div
      ref={group}
      role="group"
      tabIndex={-1}
      aria-labelledby={headingId}
      aria-describedby={keysId}
      onKeyDown={event => transportKey(event, transport)}
      className={LISTENING_CARD}
    >
      <div className="flex flex-col gap-1 px-4 pt-4">
        <h3 id={headingId} className={MICRO_LABEL}>
          {t(`${COPY}.transport.label`)}
        </h3>
        <p className={cn(TEXT_CAPTION, 'text-muted-foreground')}>{t(`${COPY}.transport.clip`)}</p>
        <p id={keysId} className="sr-only">
          {t(`${COPY}.transport.keys`)}
        </p>
      </div>
      <TwoSides sides={sides} transport={transport} />
      <TransportNotes transport={transport} />
      <TransportBar transport={transport} disabled={silent} />
    </div>
  )
}
